import {
  BuildingIcon,
  CalendarDaysIcon,
  LayersIcon,
  LayoutDashboardIcon,
  TargetIcon,
  UsersIcon,
  type LucideIcon,
} from 'lucide-react'
import { matchPath } from 'react-router-dom'
import { Role } from '@/auth/roles'

export type NavItem = {
  to: string
  /** Key into the `nav` section of the catalogue, resolved at render time. */
  labelKey:
    | 'nav.dashboard'
    | 'nav.leads'
    | 'nav.customers'
    | 'nav.tradeFairs'
    | 'nav.catalog'
    | 'nav.users'
  icon: LucideIcon
  /** Match the path exactly. Only the index route needs it. */
  end?: boolean
  /** Minimum role. Omitted means every authenticated user sees it. */
  role?: Role
}

/**
 * One list, two readers: the sidebar renders it, the header resolves the current breadcrumb from
 * it. A route added in only one of the two places is the failure this avoids.
 */
export const navigation: NavItem[] = [
  // The order a day at a fair runs in: where am I, which fair is on, what did I write there, and
  // which of it has reached the master data. Maintenance sits at the bottom, below that arc.
  { to: '/', labelKey: 'nav.dashboard', icon: LayoutDashboardIcon, end: true },
  // Everyone who captures leads needs the fairs — a lead cannot be created without picking one.
  { to: '/trade-fairs', labelKey: 'nav.tradeFairs', icon: CalendarDaysIcon, role: Role.SALES },
  // Hidden rather than disabled: a link that always 403s is worse than no link. The backend
  // enforces the same rule regardless.
  { to: '/leads', labelKey: 'nav.leads', icon: TargetIcon, role: Role.SALES },
  // Sales owns the master data too: a company met at a stand has to be enterable without an
  // administrator standing next to the booth.
  { to: '/customers', labelKey: 'nav.customers', icon: BuildingIcon, role: Role.SALES },
  // The catalogue is maintenance, not day-to-day work: the picker inside the lead form is where
  // sales meets it. Reading it is allowed for them, having a page for it is not useful.
  { to: '/product-catalog', labelKey: 'nav.catalog', icon: LayersIcon, role: Role.ADMIN },
  { to: '/admin', labelKey: 'nav.users', icon: UsersIcon, role: Role.ADMIN },
]

/**
 * The entry the given path belongs to, or undefined for a route outside the navigation.
 *
 * `matchPath` rather than a string comparison of our own: it is the same matcher the routes and
 * the NavLinks run on, so the breadcrumb and the highlighted item cannot come to different
 * conclusions once an entry grows a dynamic segment.
 */
export function activeItem(pathname: string): NavItem | undefined {
  return navigation.find((item) => matchPath({ path: item.to, end: item.end ?? false }, pathname))
}
